import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react";
import keycode from "keycode";
import { AxiosResponse, AxiosError } from "axios";
import classNames from "classnames";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { debounce } from "underscore";
import $ from "jquery";

import SearchResult from "@/components/SearchAndResultShared/SearchResult";
import Loader from "@/components/loader";
import AutocompleteSearch from "@/app/pages/businessDirectory/AutocompleteSearch";
import Breadcrumb from "@/components/breadCrumb";
import PaginationElementComponent from "@/components/PaginationElementComponent";
import { AppContext, ContextType } from "@/shared/context";
import { useFocus, useDidUpdateEffect } from "@/shared/customHooks";
import {
  addViewportIsMobileListener,
  trackRefineSearchClickEvents,
  isNotUndefined,
  isUndefined,
  isUndefinedOrNull,
  trackNullSearchResults,
  trackGTMSearchSubmitEvents,
  dnbComLog,
  LogSeverity,
} from "@/shared/FunctionCore";
import { filterOutUndefinedsObj } from "@/shared/Internal";
import {
  TabContentsComponentProps,
  CompaniesResult,
  Country,
  NumberOfEmployeesRange,
  FreeRoles,
} from "@/app/pages/businessDirectory/IBusinessDirectory";
import {
  getTypeaheadSearchAPIData,
  getCompanySearchAPIData,
} from "@/services/businessDirectory";
import Overlay from "@/components/overlay";

type TabCompanyProfilesTabContentsComponentProperties =
  TabContentsComponentProps & {
    inTabAll: boolean;
    getPageNumberFragmentParameterName: (tab: string) => string;
    getSearchFragmentParameterName: (tab: string) => string;
    isBusinessDirectoryLandingPage: boolean;
    languageCode: string,
    isSiteSearchResultsPage: boolean
  };

const BusinessDirectorySearch = ({
  inTabAll,
  isSearchOverlay,
  isSiteSearchResultsPage,
  isSearchOverlayOpen,
  activeTab,
  isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage,
  isBusinessDirectoryLandingPage,
  countryCode,
  languageCode,
  resourcePath,
  getPageNumberFragmentParameterName,
  useFragmentAndUrlState,
  setCompanyProfilesSearchValue,
  companyProfilesSearchValue,
  singlePageSearchData: { siteSearchResultsPathRewritten },
  singlePageSearchData,
}: TabCompanyProfilesTabContentsComponentProperties): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const inputIconContainerReference = useRef(null);
  const [searchInput, setSearchInput] = useState<string>("");
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [isTypeAheadloading, setIsTypeAheadloading] = useState<boolean>(false);
  const [showTypeahead, setShowTypeahead] = useState<boolean>(false);
  const [isMobileWidth, setIsMobileWidth] = useState(false);
  const [listening, setListening] = useState(false);

  const [companySearchServletResponse, setCompanySearchServletResponse] =
    useState<CompaniesResult | undefined>();
  const [typeAheadResponse, setTypeAheadResponse] = useState<
    CompaniesResult | undefined
  >();
  const [focusReference, doFocus] = useFocus<HTMLInputElement>();
  const [countryArray, setCountryArray] = useState<Country[]>([]);
  const [numberOfEmployeesRangeArray, setNumberOfEmployeesRangeArray] =
    useState<NumberOfEmployeesRange[]>([]);
  const [freeRolesArray, setFreeRolesArray] = useState<FreeRoles[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>();
  const [selectedFreeRoles, setSelectedFreeRoles] = useState<
    FreeRoles | undefined
  >();
  const [selectedNumberOfEmployeesRange, setSelectedNumberOfEmployeesRange] =
    useState<NumberOfEmployeesRange | undefined>();
  const [countryRegion, setCountryRegion] = useState("");
  const [scrollToSearchResultsContainer, setScrollToSearchResultsContainer] =
    useState<boolean>(false);

  const thisTab = "Business Directory";

  const [pageNumberString, setPageNumberString] = useFragmentAndUrlState(
    getPageNumberFragmentParameterName(thisTab),
    "1"
  );
  const [pageNumber, setPageNumber] = [
    window.Number.parseInt(pageNumberString),
    (number: number) => setPageNumberString(number.toString()),
  ];

  const pageLimit = 40;
  const pageSize = inTabAll ? 5 : 25;
  const location = useLocation(); // Get the current location object
  const [searchInputStringFromUrl, setSearchInputStringFromUrl] = useState("");
  const [businessDirectorySearchParam, setbusinessDirectorySearchParam] = useState("");

  useEffect(() => {
    // Function to handle back button or URL change
    const handleLocationChange = async () => {
      const hash = location.hash.substring(1);  // Removes the "#"
      // Create URLSearchParams instance to parse the hash
      const hashParams = new URLSearchParams(hash);
      // Extract specific parameters from the hash
      const businessDirectorySearch: any = hashParams.get("BusinessDirectorySearch");
      setbusinessDirectorySearchParam(businessDirectorySearch);
      if (businessDirectorySearch?.length && searchInput !== businessDirectorySearch) {
        setSearchInputStringFromUrl(businessDirectorySearch);
      } else {
        setSearchInputStringFromUrl("");
      }

    };
    handleLocationChange();
  }, [location]);

  useEffect(() => {
    if (searchInputStringFromUrl?.length) {
      getCompanySearchData();
    }

  }, [searchInputStringFromUrl])

  const executeTypeaheadSearchServletRequestDebounced = useCallback(
    debounce((debounceSearchTerm: string) => {
      getTypeaheadSearchData(debounceSearchTerm)
    }, 200),
    []);

  const { isBDTabClicked, isEuBd } = useContext(AppContext) as ContextType

  useEffect(() => {
    if (isBDTabClicked && isSearchOverlay) { // isSearchOverlay = IsBDLanding Page
      setSearchInput("")
    }
  }, [isBDTabClicked])

  useEffect(() => {
    if (countryCode !== "pl" && searchInput) {
      if (searchInput.length > 2) {
        executeTypeaheadSearchServletRequestDebounced(searchInput)
      } else {
        setTypeAheadResponse(void 0);
      }
    }
    // }, [resourcePath, searchInput, tabIsActive]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    getCompanySearchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    pageNumber,
    inTabAll,
    isSearchOverlay,
    resourcePath,
    siteSearchResultsPathRewritten,
    isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage,
    countryCode,
  ]);
  // searchInput,  were also included in dependency array

  useEffect(() => {
    addViewportIsMobileListener((isMobile) => setIsMobileWidth(isMobile));
  }, []);

  const searchResultsContainerReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      ((isSearchOverlay && isSearchOverlayOpen) ||
        (isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage &&
          !isSearchOverlay)) &&
      activeTab === thisTab
    ) {
      doFocus();
    }
  }, [
    isSearchOverlay,
    isSearchOverlayOpen,
    activeTab,
    doFocus,
    isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage,
  ]);

  useDidUpdateEffect(() => {
    if (
      !isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage &&
      !isSearchOverlay &&
      activeTab === thisTab
    ) {
      doFocus();
    }
  }, [
    activeTab,
    isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage,
    isSearchOverlay,
  ]);

  useDidUpdateEffect(() => {
    if (
      !isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage &&
      !isSearchOverlay
    ) {
      setPageNumber(1);
      getCompanySearchData(1);
    }
  }, [
    selectedCountry?.countryISOAlpha2Code,
    selectedFreeRoles?.familyTreeRolesPlayed,
    selectedNumberOfEmployeesRange?.maximumValue,
    selectedNumberOfEmployeesRange?.minimumValue,
  ]);
  // searchInput

  useEffect(() => {
    if(companyProfilesSearchValue) {
      setSearchInput(companyProfilesSearchValue);
    }
  }, [companyProfilesSearchValue]);

  const oneOrMoreFiltersSet =
    selectedCountry || selectedFreeRoles || selectedNumberOfEmployeesRange;
  const placeholderOptionIndex = -1;

  const countryIndexOfArray = useMemo(() => {
    const countriesMapped = countryArray.map((co) => ({
      country: co,
      indexOf: co.countryName
        .toLowerCase()
        .indexOf(countryRegion.toLowerCase()),
    }));

    const starting = countriesMapped.filter((va) => va.indexOf === 0);
    const includingButNotStarting = countriesMapped.filter(
      (va) => va.indexOf > 0
    );

    const startingSorted = starting.sort((a, b) =>
      a.country.countryName.localeCompare(b.country.countryName)
    );
    const includingButNotStartingSorted = includingButNotStarting.sort((a, b) =>
      a.country.countryName.localeCompare(b.country.countryName)
    );

    return [...startingSorted, ...includingButNotStartingSorted];
  }, [countryArray, countryRegion]);

  const showNumberOfEmployeesFilter = () =>
    isNotUndefined(
      numberOfEmployeesRangeArray.find(
        (numberOfEmployees: NumberOfEmployeesRange) =>
          numberOfEmployees.candidatesMatchedQuantity > 0
      )
    );

  const safeEncodeURI = (data) => {
    // to decode data
    const decodedData = decodeURI(data);
    // if decoded data is same as encoded data
    if (decodedData === data) {
      // data is not encoded, so encoding it 
      return encodeURI(data);
    }
    return data;
  }

  const getCompanySearchData = (currentPage?: number) => {
    const searchText = searchInput || companyProfilesSearchValue;
    const countryISOAlpha2CodeSelected = selectedCountry?.countryISOAlpha2Code;
    // if loading is true mean API is already fired
     if (!loadingState) {
      if(searchText.length > 0){
        try {
          setLoadingState(true);
          const params = {
            companyname: safeEncodeURI(searchText),
            countryisoalphatwocode: isEuBd ? countryCode : ( countryISOAlpha2CodeSelected ? countryISOAlpha2CodeSelected : null),
            languagecode: languageCode,
            familytreerolesplayed: selectedFreeRoles?.familyTreeRolesPlayed,
            maximumnumberofemployees:
            selectedNumberOfEmployeesRange?.maximumValue,
            minimumnumberofemployees:
            selectedNumberOfEmployeesRange?.minimumValue,
            pagenumber: currentPage || pageNumber,
            pagesize: pageSize,
            // resourcePath: resourcePath,
            returnnav: true,
            // searchTerm: searchInput,
            // token: token,
            // isMarketable: true,
          };
          const apiParams = filterOutUndefinedsObj(params);
          getCompanySearchAPIData(apiParams)
            .then((ax: AxiosResponse) => {
              if (ax && ax.data !== null) {
                setCompanySearchServletResponse(ax.data);
                const navigators = ax.data.navigators;
                if (navigators) {
                  const { countries, familyTreeRolesPlayed, numberOfEmployees } =
                    navigators;
                  if (numberOfEmployees.length > 0) {
                    if (
                      !numberOfEmployees.some(
                        (numOfEmployees: NumberOfEmployeesRange) =>
                          numOfEmployees.candidatesMatchedQuantity > 0
                      )
                    ) {
                      setSelectedNumberOfEmployeesRange(void 0);
                    }
                    // isNotUndefined(numberOfEmployees.find((numOfEmployees: NumberOfEmployeesRange) => numOfEmployees.candidatesMatchedQuantity > 0)) ? "" : setSelectedNumberOfEmployeesRange(void 0) ;
                  } else {
                    setSelectedNumberOfEmployeesRange(void 0);
                  }
                  setNumberOfEmployeesRangeArray(
                    isNotUndefined(numberOfEmployees) &&
                      numberOfEmployees !== null
                      ? numberOfEmployees
                      : []
                  );
                  setFreeRolesArray(
                    isNotUndefined(familyTreeRolesPlayed) &&
                      familyTreeRolesPlayed !== null
                      ? familyTreeRolesPlayed
                      : []
                  );
                  if (
                    isUndefined(selectedCountry) &&
                    countries !== null &&
                    isNotUndefined(countries)
                  ) {
                    setCountryArray(countries);
                  }
                  // if (isSearchOverlay) { // true when we are on BD Landing page
                  // 	navigate(
                  // 		`/de-de/seitensuche-ergebnisse.html#BusinessDirectorySearch=${searchInput}`,
                  // 		{ state: { routedCompanyDataResponse: ax.data }});
                  // }
                } else {
                  setCountryArray([]);
                  setFreeRolesArray([]);
                  setNumberOfEmployeesRangeArray([]);
                }
                if (scrollToSearchResultsContainer) {
                  const { current } = searchResultsContainerReference;
                  if (current !== null) {
                    const $window = $(window)
                    const position = $(current).offset()!.top - $window.height()! / 2
                    $window.scrollTop(position);
                  }
                  setScrollToSearchResultsContainer(false);
                }
                if (ax.data.companies.length === 0 && activeTab !== "All") {
                  trackNullSearchResults();
                }
              }
              setLoadingState(false);
            })
            .catch((error: AxiosError) => {
              dnbComLog(LogSeverity.ERROR, error);
              setLoadingState(false);
              setCompanySearchServletResponse(void 0); 
            });
        } catch {
          setLoadingState(false);
        }
      }else{
        resetInputs();
      }
    }
  };

  const getShowMoreData = () => {
    if (isSearchOverlay || searchInput !== companyProfilesSearchValue) {
      // isSearchOverlay is true on BD landing page
      onSearch();
    } else setShowTypeahead(false);
  };

  const getTypeaheadSearchData = (autocompleteInputValue: string) => {
    if (autocompleteInputValue.length > 0) {
      try {
        setIsTypeAheadloading(true);
        getTypeaheadSearchAPIData(decodeURIComponent(autocompleteInputValue.toString().replace(/%/g,'~~pct~~')), countryCode, languageCode)
          .then((ax: AxiosResponse) => {
            setIsTypeAheadloading(false);
            if (ax && ax.data !== null) {
              setTypeAheadResponse(ax.data);
            }
          })
          .catch((error: AxiosError) => {
            dnbComLog(LogSeverity.ERROR, error);
            setIsTypeAheadloading(false);
            setTypeAheadResponse(void 0);
          });
      } catch {
        setIsTypeAheadloading(false);
      }
    }
  };

  const resetInputs = () => {
    setPageNumber(1)
    setCompanySearchServletResponse(void 0);
    setCountryArray([]);
    setFreeRolesArray([]);
    setNumberOfEmployeesRangeArray([]);
  }

  const onSearch = () => {
    setCompanyProfilesSearchValue(searchInput);
    setShowTypeahead(false);
    if (isBusinessDirectoryLandingPage) {
      trackGTMSearchSubmitEvents(searchInput, "searchSubmitDirectory", "searchQueryDirectory");
    }
    if (isSiteSearchResultsPage) {
      trackGTMSearchSubmitEvents(searchInput);
    }
    resetInputs()

    if (searchInput.length >= 2 || companyProfilesSearchValue.length >= 2) {
      if (isSearchOverlay) {
        // true on BD landing page

        navigate(
          `${t("businessDirectory.siteSearchResultPagePath")}.html#BusinessDirectorySearch=${searchInput}`
        );
      } else {
        getCompanySearchData();
      }
    }
  };

  useEffect(() => {
    if (listening) {
      return;
    }

    const referenceCurrent = inputIconContainerReference.current;

    if (referenceCurrent === null) {
      return;
    }

    setListening(true);

    for (const st of ["click", "touchstart"]) {
      window.document.addEventListener(st, (ev) => {
        const target: any = ev.target;

        if (!referenceCurrent.contains(target)) {
          setShowTypeahead(false);
        }
      });
    }
  }, [listening]);

  const breadcrumbs = [
    {
      name: t("businessDirectory.SearchResultStartBreadcrumb"),
      url: "/"
    },
    {
      name: t("business_directory"),
      url: t("bdLandingPageRedirectionLink"),
    },
    {
      name: t("businessDirectory.companyProfileTab"),
      url: ""
    },
    // { name: inTabAll ? "Top Results" : "Firmenprofile", url: "" },
  ];

  const renderPagination = () => (
    <PaginationElementComponent
      currentPage={pageNumber}
      currentPageValueClassName="currentPageValue"
      ellipsesClassName="ellipses"
      ltgtClassName="ltgt"
      nextButtonClassName="nextButton"
      nextClassName="next"
      onPageChange={(nu) => {
        setPageNumber(nu);
        setScrollToSearchResultsContainer(true);
      }}
      pageValueButtonClassName="pageValueButton"
      paginationContainerRef={void 0}
      previousButtonClassName="previousButton"
      previousClassName="previous"
      siblings={isMobileWidth ? 3 : 5}
      singlePageSearchData={singlePageSearchData}
      totalPages={
        companySearchServletResponse
          ? window.Math.min(
            pageLimit,
            window.Math.ceil(
              (companySearchServletResponse.totalMatchedResults || 0) / pageSize
            )
          )
          : 1
      }
      wrapperClassName="paginationWrapper"
    />
  );

  return (
    <>
      {!inTabAll && (
        <div
          className={
            isBusinessDirectoryLandingPage ||
              (isSiteSearchResultsPage && searchInput.length === 0)
              ? "tabNameWrapper"
              : "tabNameWrapperWithoutBorderRadius"
          }
        >
          {/* <div className={(isSearchOverlay || (isSiteSearchResultsPage && searchInput.length === 0)) ? "tabNameWrapper" : "tabNameWrapperWithoutBorderRadius"}> */}
          <div className="search-flex-container">
            <div
              className="input-icon-container text-box-style"
              onBlur={(fo) => {
                const currentTarget = fo.currentTarget;
                const relatedTarget = fo.relatedTarget;

                if (
                  relatedTarget !== null &&
                  !currentTarget.contains(relatedTarget)
                ) {
                  setShowTypeahead(false);
                }
              }}
              ref={inputIconContainerReference}
            // ref={reference}
            >
              {/* // =========Search Form========= */}
              <div className="text-field-container">
                <input
                  className="input-with-icon single-input-icon"
                  onChange={(ch) => {
                    const value = ch.target.value;

                    if (value.length >= 2 && value.length > searchInput.length) {
                      setShowTypeahead(true);
                    }

                    setSearchInput(encodeURIComponent(ch.target.value));

                    if (value.length === 0) {
                      setShowTypeahead(false);
                    }
                  }}
                  onFocus={() => {
                    if (searchInput.length) {
                      setShowTypeahead(true)
                    } else {
                      setShowTypeahead(false);
                      setTypeAheadResponse(void 0)
                    }
                  }}
                  onKeyPress={(ke) => {
                    if (ke.which === keycode.codes.enter) {
                      onSearch();
                    }
                  }}
                  placeholder={`${t("businessDirectory.searchHere")}....`}
                  ref={focusReference}
                  type="text"
                  value={decodeURIComponent(searchInput)}
                />

                {searchInput && searchInput.length > 0 ? (
                  <button
                    className="close-icon"
                    onClick={() => {
                      setSearchInput("");
                      setShowTypeahead(false);
                      setTypeAheadResponse(void 0)
                    }}
                    type="button"
                  />
                ) : (
                  <></>
                )}
              </div>

              <button className="search-btn" onClick={onSearch} type="button">
                {t("businessDirectory.search")}
              </button>
              {typeAheadResponse &&
                typeAheadResponse.companies && (
                  <AutocompleteSearch
                    typeAheadResponse={typeAheadResponse}
                    showTypeahead={showTypeahead}
                    setShowTypeahead={setShowTypeahead}
                    getShowMoreData={getShowMoreData}
                  />
                )}
            </div>
          </div>
        </div>
      )}

      {!isSearchOverlay && (
        <div className="contentWrapper">
          {!inTabAll &&
            companySearchServletResponse &&
            (countryArray.length > 0 ||
              numberOfEmployeesRangeArray.length > 0 ||
              freeRolesArray.length > 0) ? (
            <div className="filter-text-flex-container">
              <div className="refine-text">
                <h6>{t("businessDirectory.filterHeadingRefineYourSearch")}</h6>
              </div>

              {oneOrMoreFiltersSet ? (
                <button
                  className="clearAllButton"
                  onClick={() => {
                    setCountryRegion("");
                    setSelectedCountry(void 0);
                    setSelectedFreeRoles(void 0);
                    setSelectedNumberOfEmployeesRange(void 0);
                  }}
                  type="button"
                >
                  {t("businessDirectory.clearAll") || "Clear All"}
                </button>
              ) : (
                <></>
              )}

              <div className="main-flex-container">
                <div
                  className={classNames({
                    "main-flex-item hidden-md hidden-lg tabs-arrow-wrap": true,
                    "mt-28": oneOrMoreFiltersSet,
                  })}
                >
                  <div className="tabs-left-arrow">
                    <button
                      className="tabs-left-arrow-icon tabs-arrow-icon"
                      type="button"
                    />
                  </div>
                </div>

                <div className="inner-flex-container">
                  <div className="inner-display-container">
                    {!isEuBd ? (
                      <div className="inner-flex-item">
                        {oneOrMoreFiltersSet ? (
                          <button
                            className={
                              selectedCountry ? "selectedFilter" : "emptyFilter"
                            }
                            onClick={() => {
                              setSelectedCountry(void 0);
                            }}
                            type="button"
                          >
                            {selectedCountry ? selectedCountry.countryName : ""}

                            <span className="filterX">X</span>
                          </button>
                        ) : (
                          <></>
                        )}

                        <input
                          className="enterCountryRegionInput"
                          onChange={(ch) => {
                            const value = ch.target.value;
                            setCountryRegion(value);
                          }}
                          onClick={() => {
                            trackRefineSearchClickEvents("Country/Region");
                          }}
                          onKeyDown={(ke) => {
                            if (
                              ke.which === keycode.codes.enter &&
                              countryIndexOfArray.length > 0
                            ) {
                              setCountryRegion("");
                              setSelectedCountry(
                                countryIndexOfArray[0]!.country
                              );
                            } else {
                              const target = ke.target;
                              if (!(target instanceof HTMLInputElement)) {
                                setCountryRegion("")
                                return;
                              }
                              setCountryRegion(target.value);
                            }
                          }}
                          placeholder="Enter Country/Region"
                          type="text"
                          value={countryRegion}
                        />

                        {countryRegion &&
                          countryRegion.length > 0 &&
                          countryIndexOfArray.length > 0 ? (
                          <div className="countryListContainer">
                            {countryIndexOfArray.map((va, index) => (
                              <button
                                className="countryListItem"
                                key={index}
                                onClick={() => {
                                  setCountryRegion("");
                                  setSelectedCountry(va.country);
                                }}
                                type="button"
                              >
                                {va.country.countryName} (
                                {va.country.candidatesMatchedQuantity})
                              </button>
                            ))}
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    ) : (
                      <></>
                    )}
                    {selectedNumberOfEmployeesRange ||
                      showNumberOfEmployeesFilter() ? (
                      <div className="inner-flex-item">
                        {oneOrMoreFiltersSet ? (
                          <button
                            className={
                              selectedNumberOfEmployeesRange
                                ? "selectedFilter"
                                : "emptyFilter"
                            }
                            onClick={() => {
                              setSelectedNumberOfEmployeesRange(void 0);
                            }}
                            type="button"
                          >
                            {isNotUndefined(selectedNumberOfEmployeesRange)
                              ? isUndefinedOrNull(
                                selectedNumberOfEmployeesRange.maximumValue
                              )
                                ? `> ${selectedNumberOfEmployeesRange.minimumValue}`
                                : `${selectedNumberOfEmployeesRange.minimumValue}-${selectedNumberOfEmployeesRange.maximumValue}`
                              : ""}
                            <span className="filterX">X</span>
                          </button>
                        ) : (
                          <></>
                        )}

                        <select
                          className="selectNumberOfEmployeesSelect"
                          onChange={(ch) => {
                            setSelectedNumberOfEmployeesRange(
                              window.JSON.parse(ch.target.value)
                            );
                            trackRefineSearchClickEvents("Number of Employees");
                          }}
                          value={
                            isUndefined(selectedNumberOfEmployeesRange)
                              ? placeholderOptionIndex
                              : window.JSON.stringify(
                                selectedNumberOfEmployeesRange
                              )
                          }
                        >
                          <option
                            disabled={true}
                            value={placeholderOptionIndex}
                          >
                            {t(
                              "businessDirectory.selectEmployeeFilterPlaceholder"
                            )}
                          </option>
                          {numberOfEmployeesRangeArray.map((nu, num) =>
                            nu.candidatesMatchedQuantity > 0 ? (
                              <option
                                key={num}
                                value={window.JSON.stringify(nu)}
                              >
                                {isUndefinedOrNull(nu.maximumValue)
                                  ? `> ${nu.minimumValue}`
                                  : `${nu.minimumValue}-${nu.maximumValue}`}{" "}
                                ({nu.candidatesMatchedQuantity})
                              </option>
                            ) : (
                              <></>
                            )
                          )}
                        </select>
                      </div>
                    ) : (
                      <></>
                    )}

                    <div className="inner-flex-item">
                      {oneOrMoreFiltersSet ? (
                        <button
                          className={
                            selectedFreeRoles ? "selectedFilter" : "emptyFilter"
                          }
                          onClick={() => {
                            setSelectedFreeRoles(void 0);
                          }}
                          type="button"
                        >
                          {selectedFreeRoles
                            ? selectedFreeRoles.familyTreeRolesName
                            : ""}

                          <span className="filterX">X</span>
                        </button>
                      ) : (
                        <></>
                      )}

                      <select
                        className="selectEntityTypeSelect"
                        onChange={(ch) => {
                          setSelectedFreeRoles(
                            window.JSON.parse(ch.target.value)
                          );
                          trackRefineSearchClickEvents("Entity Type");
                        }}
                        value={
                          isUndefined(selectedFreeRoles)
                            ? placeholderOptionIndex
                            : window.JSON.stringify(selectedFreeRoles)
                        }
                      >
                        <option disabled={true} value={placeholderOptionIndex}>
                          {t(
                            "businessDirectory.selectEntityTypeFilterPlaceholder"
                          )}
                        </option>

                        {freeRolesArray.map(
                          (freeRoles: FreeRoles, index: number) => (
                            <option
                              key={index}
                              value={window.JSON.stringify(freeRoles)}
                            >
                              {freeRoles.familyTreeRolesName} (
                              {freeRoles.candidatesMatchedQuantity})
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                <div
                  className={classNames({
                    "main-flex-item hidden-md hidden-lg tabs-arrow-wrap": true,
                    "mt-28": oneOrMoreFiltersSet,
                  })}
                >
                  <div className="tabs-right-arrow">
                    <button
                      className="tabs-right-arrow-icon tabs-arrow-icon"
                      type="button"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}

          <Loader
            inProgress={(!scrollToSearchResultsContainer && loadingState) || isTypeAheadloading}
          />
          {companySearchServletResponse && (
            <div ref={searchResultsContainerReference}>
              <Breadcrumb
                breadcrumbs={breadcrumbs}
                forComponent="SearchResult"
                delimiter="/"
                styling={isSiteSearchResultsPage ? "text-capitalize font-style-medium" : ""}
              />
              <SearchResult
                resultData={companySearchServletResponse}
                inputSearchCombineString={`"${companyProfilesSearchValue}"`}
                paginationComponent={!inTabAll ? renderPagination() : <></>}
                forComponent="businessDirectory"
              />
              <Loader inProgress={scrollToSearchResultsContainer && loadingState} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BusinessDirectorySearch;
